import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type FilterValues = Record<string, string>;

/**
 * List filters stored in the URL query string, so filtered views survive reloads
 * and can be linked. Changing any filter other than `page` resets to page 1.
 */
export function useUrlFilters<T extends FilterValues>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const result: FilterValues = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const value = searchParams.get(key);
      if (value !== null) result[key] = value;
    }
    return result as T;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setValues = useCallback(
    (patch: Partial<T>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === '' || value === defaults[key]) next.delete(key);
            else next.set(key, String(value));
          }
          if (!('page' in patch)) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams],
  );

  return [values, setValues] as const;
}

export const toInt = (value: string | undefined, fallback?: number) => {
  const parsed = Number(value);
  return value && Number.isFinite(parsed) ? parsed : fallback;
};
